import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check, X as XIcon, Archive, Trash2, Newspaper, Bell, Sparkles, RefreshCw, BookOpen, ImageIcon } from "lucide-react";

const STATUSES = [
  { v: "all", label: "All" },
  { v: "pending_review", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "archived", label: "Archived" },
];

const STATUS_STYLES = {
  pending_review: { bg: "#F5C518", fg: "#1F1B16", label: "PENDING" },
  approved: { bg: "#3C9A5F", fg: "#FDFBF7", label: "APPROVED" },
  rejected: { bg: "#B23A3A", fg: "#FDFBF7", label: "REJECTED" },
  archived: { bg: "#7A7A7A", fg: "#FDFBF7", label: "ARCHIVED" },
};

export default function AdminMemorials() {
  const [status, setStatus] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [contribs, setContribs] = useState([]);
  const [aiStatus, setAiStatus] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [cardStatus, setCardStatus] = useState(null);
  const [cardRegen, setCardRegen] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);

  const loadAiStatus = async () => {
    try {
      const { data } = await http.get("/admin/memorials/ai-biographies/status");
      setAiStatus(data);
    } catch {}
    try {
      const { data } = await http.get("/admin/memorials/share-cards/status");
      setCardStatus(data);
    } catch {}
  };

  const load = () => {
    http.get("/admin/memorials", { params: { status } })
      .then((r) => { setItems(r.data.items); setCounts(r.data.counts); })
      .catch(() => toast.error("Failed to load memorials"));
  };
  useEffect(load, [status]); // eslint-disable-line

  const loadContribs = () => {
    http.get("/admin/memorial-contributions", { params: { status: "pending" } })
      .then((r) => setContribs(r.data.items || []));
  };
  useEffect(loadContribs, []);
  useEffect(() => { loadAiStatus(); }, []);

  useEffect(() => {
    if (!regenerating && !cardRegen) return;
    const iv = setInterval(loadAiStatus, 5000);
    return () => clearInterval(iv);
  }, [regenerating, cardRegen]);

  useEffect(() => {
    if (regenerating && aiStatus && aiStatus.missing === 0) {
      setRegenerating(false);
      toast.success("Biography backfill complete");
    }
  }, [aiStatus, regenerating]);

  useEffect(() => {
    if (cardRegen && cardStatus && cardStatus.missing === 0) {
      setCardRegen(false);
      toast.success("Share card backfill complete");
    }
  }, [cardStatus, cardRegen]);

  const regenerateBios = async (mode) => {
    const msg = mode === "all"
      ? `Regenerate biographies for ALL ${aiStatus?.total_approved || "?"} approved memorials? Uses Claude Sonnet 4.5.`
      : `Generate biographies for ${aiStatus?.missing || "?"} memorials without one? Runs in the background.`;
    if (!window.confirm(msg)) return;
    setRegenerating(true);
    try {
      await http.post(`/admin/memorials/ai-biographies/regenerate?mode=${mode}`);
      toast.success("Queued — polling every 5s");
      setTimeout(() => setRegenerating(false), 30 * 60 * 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setRegenerating(false);
    }
  };

  const regenerateOneBio = async () => {
    if (!selected) return;
    setBioBusy(true);
    try {
      await http.post(`/admin/memorials/${selected.id}/ai-biography`);
      toast.success("Biography queued — refresh in ~30s to see it");
      setTimeout(loadAiStatus, 45000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Biography generation failed"); }
    finally { setBioBusy(false); }
  };

  const regenerateCards = async (mode) => {
    const msg = mode === "all"
      ? `Regenerate share cards for ALL ${cardStatus?.total_approved || "?"} approved memorials? Uses Nano Banana.`
      : `Generate share cards for ${cardStatus?.missing || "?"} memorials without one? Runs in the background.`;
    if (!window.confirm(msg)) return;
    setCardRegen(true);
    try {
      await http.post(`/admin/memorials/share-cards/regenerate?mode=${mode}`);
      toast.success("Queued — polling every 5s");
      setTimeout(() => setCardRegen(false), 30 * 60 * 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setCardRegen(false);
    }
  };

  const regenerateOneCard = async () => {
    if (!selected) return;
    setCardBusy(true);
    try {
      await http.post(`/admin/memorials/${selected.id}/share-card`);
      toast.success("Share card queued — refresh in ~30s to see it");
      setTimeout(loadAiStatus, 45000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Card generation failed"); }
    finally { setCardBusy(false); }
  };

  const act = async (newStatus) => {
    try {
      await http.patch(`/admin/memorials/${selected.id}`, { status: newStatus, comment });
      toast.success(`Marked ${newStatus}`);
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this memorial and all contributions permanently?")) return;
    try {
      await http.delete(`/admin/memorials/${selected.id}`, { params: { comment } });
      toast.success("Deleted");
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const refreshNews = async () => {
    try {
      const { data } = await http.post(`/admin/memorials/${selected.id}/refresh-news`);
      toast.success(`Added ${data.added || 0} news mention(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "News scrape failed"); }
  };

  const sendAnniversaries = async () => {
    if (!window.confirm("Send anniversary reminder emails for today's birthdays / death anniversaries to all followers?")) return;
    try {
      const { data } = await http.post("/admin/memorials/anniversaries/send-today");
      toast.success(`Processed ${data.memorials_processed || 0} memorials, sent ${data.emails_sent || 0} emails`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const fixSlugs = async () => {
    if (!window.confirm("Sanitize any memorial slugs containing slashes / parens / apostrophes. Old slugs are kept as aliases so bookmarked URLs keep resolving. Continue?")) return;
    try {
      const { data } = await http.post("/admin/memorials/fix-slugs");
      toast.success(`Slug repair: scanned ${data.scanned}, renamed ${data.renamed}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Slug repair failed"); }
  };

  const moderateContrib = async (cid, newStatus) => {
    try {
      await http.patch(`/admin/memorial-contributions/${cid}`, { status: newStatus });
      toast.success(`Contribution ${newStatus}`);
      loadContribs();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-memorials-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline mb-2">CELEBRATE A LIFE</div>
          <h2 className="font-display text-3xl">Memorials moderation</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={fixSlugs} data-testid="admin-mem-fix-slugs" className="wofy-btn-ghost text-xs" title="Repair external memorial slugs (Manorama / Tributes) that contain slashes or unsafe characters.">
            <RefreshCw className="w-3 h-3" /> Repair broken slugs
          </button>
          <button onClick={sendAnniversaries} data-testid="admin-mem-send-anniv" className="wofy-btn-ghost text-xs">
            <Bell className="w-3 h-3" /> Send today's anniversary emails
          </button>
        </div>
      </div>

      {/* AI biography toolbar (mirrors AdminRecipes / AdminOrganizations) */}
      <div className="mt-6 p-4 border rounded-lg flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--wofy-line)", background: "rgba(196,127,58,0.08)" }}>
        <div className="text-sm min-w-0">
          <div className="font-semibold flex items-center gap-2">
            <Sparkles size={14} style={{ color: "#c47f3a" }} /> AI biographies (Claude Sonnet 4.5)
          </div>
          {aiStatus ? (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              {aiStatus.with_biography} of {aiStatus.total_approved} approved memorials have an AI biography · {aiStatus.missing} still missing
            </div>
          ) : (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>Loading status…</div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-mem-bio-regen-missing"
            onClick={() => regenerateBios("missing")}
            disabled={regenerating || (aiStatus && aiStatus.missing === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#eee" : "#c47f3a", color: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#888" : "white" }}
          >
            <RefreshCw size={13} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Running…" : `Generate for missing${aiStatus ? ` (${aiStatus.missing})` : ""}`}
          </button>
          <button
            data-testid="admin-mem-bio-regen-all"
            onClick={() => regenerateBios("all")}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <Sparkles size={13} /> Regenerate ALL
          </button>
        </div>
      </div>

      {/* Shareable memorial-card toolbar (Nano Banana) */}
      <div className="mt-3 p-4 border rounded-lg flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--wofy-line)", background: "rgba(96,150,180,0.08)" }}>
        <div className="text-sm min-w-0">
          <div className="font-semibold flex items-center gap-2">
            <ImageIcon size={14} style={{ color: "#4a86a6" }} /> Shareable tribute cards (Nano Banana)
          </div>
          {cardStatus ? (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              {cardStatus.with_card} of {cardStatus.total_approved} approved memorials have a share card · {cardStatus.missing} still missing
            </div>
          ) : (
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>Loading status…</div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-mem-card-regen-missing"
            onClick={() => regenerateCards("missing")}
            disabled={cardRegen || (cardStatus && cardStatus.missing === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: (cardRegen || (cardStatus && cardStatus.missing === 0)) ? "#eee" : "#4a86a6", color: (cardRegen || (cardStatus && cardStatus.missing === 0)) ? "#888" : "white" }}
          >
            <RefreshCw size={13} className={cardRegen ? "animate-spin" : ""} />
            {cardRegen ? "Running…" : `Generate for missing${cardStatus ? ` (${cardStatus.missing})` : ""}`}
          </button>
          <button
            data-testid="admin-mem-card-regen-all"
            onClick={() => regenerateCards("all")}
            disabled={cardRegen}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <ImageIcon size={13} /> Regenerate ALL
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s.v} onClick={() => setStatus(s.v)} data-testid={`admin-mem-status-${s.v}`}
                  className={`px-3 py-1.5 text-sm rounded-full ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                  style={status === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}>
            {s.label} <span className="ml-1 text-xs font-mono opacity-60">{s.v === "all" ? (counts.all || 0) : (counts[s.v] || 0)}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nothing here.</p>
        ) : items.map((m) => {
          const st = STATUS_STYLES[m.status] || { bg: "#666", fg: "#fff", label: (m.status || "").toUpperCase() };
          const isPending = m.status === "pending_review";
          return (
            <div key={m.id} data-testid={`admin-memorial-${m.id}`} className="wofy-card cursor-pointer hover:shadow-md" onClick={() => { setSelected(m); setComment(""); }} style={isPending ? { borderLeft: "4px solid #F5C518" } : {}}>
              <div className="flex items-start gap-4">
                {m.portrait && <img src={m.portrait} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase font-semibold" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                    <span className="wofy-tag text-[10px]">{m.privacy}</span>
                    {m.captcha_verified === false && <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase" style={{ background: "#B23A3A", color: "#FDFBF7" }}>⚠ NO CAPTCHA</span>}
                  </div>
                  <div className="font-display text-lg mt-1">{m.full_name} <span className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>{[m.birth_date?.slice(0, 4), m.death_date?.slice(0, 4)].filter(Boolean).join("–")}</span></div>
                  {m.occupation && <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>{m.occupation}</div>}
                  <div className="mt-1 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>SUBMITTED: {new Date(m.created_at).toLocaleString()} · by {m.submitter_name} &lt;{m.submitter_email}&gt;</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending contributions */}
      {contribs.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-2xl">Pending contributions ({contribs.length})</h3>
          <div className="mt-4 grid gap-3">
            {contribs.map((c) => (
              <div key={c.id} className="wofy-card">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="wofy-tag text-[10px]">{c.kind}</span>
                    <span className="ml-3 font-display">{c.author_name}</span>
                    <span className="ml-2 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>on {c.memorial_slug}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => moderateContrib(c.id, "approved")} className="wofy-btn-primary text-xs"><Check className="w-3 h-3" /> Approve</button>
                    <button onClick={() => moderateContrib(c.id, "hidden")} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }}><XIcon className="w-3 h-3" /> Hide</button>
                  </div>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-[720px] overflow-y-auto p-8" style={{ background: "var(--wofy-paper)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">MEMORIAL</div>
                <h3 className="font-display text-2xl mt-1">{selected.full_name}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>Submitted by {selected.submitter_name} · {selected.submitter_email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="wofy-btn-ghost text-xs">Close</button>
            </div>

            <div className="mt-6 wofy-card">
              {selected.short_bio && <p className="text-sm italic">{selected.short_bio}</p>}
              {selected.obituary && <>
                <div className="text-xs font-mono uppercase tracking-wider mt-3">Obituary</div>
                <p className="text-sm mt-1 whitespace-pre-wrap line-clamp-6" style={{ color: "var(--wofy-text-soft)" }}>{selected.obituary}</p>
              </>}
              <div className="mt-3 text-xs">
                <a href={`/celebrate/${selected.slug || selected.id}`} target="_blank" rel="noreferrer" className="underline">Open full memorial page →</a>
              </div>
            </div>

            <div className="mt-6 wofy-card">
              <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Moderation note (emailed to submitter)</div>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className="w-full mt-2 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="e.g. Welcome to Social Pulse. Your memorial is now live." />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act("approved")} className="wofy-btn-primary text-sm"><Check className="w-4 h-4" /> Approve & publish</button>
                <button onClick={() => act("rejected")} className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><XIcon className="w-4 h-4" /> Reject</button>
                <button onClick={() => act("archived")} className="wofy-btn-ghost text-sm"><Archive className="w-4 h-4" /> Archive</button>
                <button onClick={refreshNews} className="wofy-btn-ghost text-sm"><Newspaper className="w-4 h-4" /> Scrape news</button>
                <button
                  onClick={regenerateOneBio}
                  disabled={bioBusy}
                  data-testid="admin-mem-bio-regen-one"
                  className="wofy-btn-ghost text-sm"
                  style={{ color: "#c47f3a" }}
                  title="Weave an AI biography from the family's data"
                >
                  <BookOpen className="w-4 h-4" /> {bioBusy ? "Weaving…" : (selected.ai_biography ? "Regenerate biography" : "Generate biography")}
                </button>
                <button
                  onClick={regenerateOneCard}
                  disabled={cardBusy}
                  data-testid="admin-mem-card-regen-one"
                  className="wofy-btn-ghost text-sm"
                  style={{ color: "#4a86a6" }}
                  title="Generate a shareable tribute card"
                >
                  <ImageIcon className="w-4 h-4" /> {cardBusy ? "Painting…" : (selected.share_card_url || selected.share_card ? "Regenerate card" : "Generate card")}
                </button>
                <button onClick={del} className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
